import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from "@/components/ai-elements/sources-custom"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Citation } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { ChevronDownIcon, Link2Icon } from "lucide-react"
import { useEffect, useState } from "react"
import { FilePreviewModalWithPdfViewer } from "../Modals/FilePreviewModal/FilePreviewModalWithPdfViewer"

const TOOLTIP_CONTENT_MAX_CHARS = 300

const truncateTooltipContent = (content: string): string =>
  content.length > TOOLTIP_CONTENT_MAX_CHARS
    ? `${content.slice(0, TOOLTIP_CONTENT_MAX_CHARS)}…`
    : content

interface AnswerSourceGroupProps {
  citations: Citation[]
  openInFullScreen?: boolean
  elementId?: string
  disableSourceClicks?: boolean
  forceOpen?: boolean
}

const SOURCE_PILL_CLASSES =
  "inline-flex h-6 items-center gap-1.5 rounded-full border border-transparent bg-white/4 px-2 pl-1.5 text-[11px] font-medium text-white/60 transition-colors hover:border-white/8 hover:bg-white/[0.07] hover:text-white/70"

const isCompanyProfileCitation = (c: Citation): boolean =>
  c.sourceType === "company_profile"

const isAnsweringContextCitation = (c: Citation): boolean =>
  c.sourceType === "answering_context"

export const AnswerSourceGroup: React.FC<AnswerSourceGroupProps> = ({
  citations,
  openInFullScreen = true,
  elementId,
  disableSourceClicks = false,
  forceOpen = false,
}) => {
  const { translate } = useTranslation()
  const [activeCitation, setActiveCitation] = useState<null | Citation>(null)
  const [isOpen, setIsOpen] = useState(forceOpen)

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true)
    }
  }, [forceOpen])

  const handleOpenChange = (open: boolean) => {
    if (forceOpen) {
      setIsOpen(true)
      return
    }

    setIsOpen(open)
  }

  const handleViewClick = (citation: Citation) => {
    setActiveCitation(citation)
  }

  const handleClosePreview = () => {
    setActiveCitation(null)
  }

  // Partition: company profile and answering context citations each collapse into
  // one non-clickable entry; KB citations dedupe by file + page as before.
  const profileCitation = citations.find(isCompanyProfileCitation)
  const answeringContextCitation = citations.find(isAnsweringContextCitation)
  const hasProfileCitation = profileCitation !== undefined
  const hasAnsweringContextCitation = answeringContextCitation !== undefined
  const uniqueKbCitations = citations
    .filter(
      (c) => !isCompanyProfileCitation(c) && !isAnsweringContextCitation(c),
    )
    .filter(
      (c, i, arr) =>
        arr.findIndex(
          (o) =>
            o.sourceFileId === c.sourceFileId &&
            o.sourceFilePage === c.sourceFilePage,
        ) === i,
    )

  const totalCount =
    (hasProfileCitation ? 1 : 0) +
    (hasAnsweringContextCitation ? 1 : 0) +
    uniqueKbCitations.length

  if (totalCount === 0) {
    return null
  }

  return (
    <>
      {activeCitation ? (
        <FilePreviewModalWithPdfViewer
          urlQueryId={activeCitation.sourceFileId}
          citation={activeCitation}
          setIsPreviewModalOpen={handleClosePreview}
          fullScreen={openInFullScreen}
        />
      ) : null}

      <Sources
        id={elementId}
        className={classNames(
          "text-zinc-600 mb-0!",
          disableSourceClicks ? "opacity-90" : "",
        )}
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <SourcesTrigger count={totalCount}>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-white/60">
            <Link2Icon className="size-3 shrink-0" />
            <span>
              {translate("AI_ELEMENTS/SOURCES_USED", { count: totalCount })}
            </span>
            <ChevronDownIcon className="size-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </span>
        </SourcesTrigger>
        <SourcesContent className="mt-2 flex-row flex-wrap gap-1.5">
          {hasProfileCitation && (
            <TooltipProvider key="company-profile">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Source
                      sourceType="company_profile"
                      title={translate("AI_ELEMENTS/COMPANY_PROFILE_SOURCE")}
                      className={SOURCE_PILL_CLASSES}
                    />
                  </span>
                </TooltipTrigger>
                {profileCitation?.sourceText && (
                  <TooltipContent className="max-w-sm whitespace-pre-wrap">
                    {truncateTooltipContent(profileCitation.sourceText)}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {hasAnsweringContextCitation && (
            <TooltipProvider key="answering-context">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Source
                      sourceType="answering_context"
                      title={translate("AI_ELEMENTS/ANSWERING_CONTEXT_SOURCE")}
                      className={SOURCE_PILL_CLASSES}
                    />
                  </span>
                </TooltipTrigger>
                {answeringContextCitation?.sourceText && (
                  <TooltipContent className="max-w-sm whitespace-pre-wrap">
                    {truncateTooltipContent(
                      answeringContextCitation.sourceText,
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {uniqueKbCitations.map((citation) => (
            <Source
              key={`${citation.sourceFileId}-${citation.sourceFilePage}`}
              href="#"
              title={`${citation.sourceFileName} / S.${citation.sourceFilePage}`}
              aria-disabled={disableSourceClicks}
              tabIndex={disableSourceClicks ? -1 : undefined}
              className={SOURCE_PILL_CLASSES}
              onClick={(e) => {
                e.preventDefault()
                if (disableSourceClicks) {
                  return
                }
                handleViewClick(citation)
              }}
            />
          ))}
        </SourcesContent>
      </Sources>
    </>
  )
}
